/**
 * AppICEMAP — PopupCard
 * Leaflet popup showing minimal report info.
 * Max width 280px, no close button, theme-matched.
 */

import L from "leaflet";
import { isLowKarmaReport } from "./ReportMarker.js";
import { getCategoryStyle } from "../../utils/categoryStyle.js";
import { fitPopupToMap } from "./popupFit.js";

/**
 * Compute relative time text.
 * @param {string} dateStr - ISO date string
 * @returns {string}
 */
function relativeTime(dateStr) {
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diff = now - then;

	const seconds = Math.floor(diff / 1000);
	if (seconds < 60) return "ahora mismo";

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `hace ${minutes} min`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `hace ${hours}h`;

	const days = Math.floor(hours / 24);
	if (days < 7) return `hace ${days}d`;

	return new Date(dateStr).toLocaleDateString("es-MX", {
		month: "short",
		day: "numeric",
	});
}

/**
 * Build the HTML content for a report popup.
 * @param {object} report
 * @returns {string}
 */
export function buildPopupContent(report) {
	const category = report.category || {};
	const { color } = getCategoryStyle(category);
	const catLabel = category.label || "Reporte";
	const time = relativeTime(report.created_at);
	const confirmations = report.confirmation_count || 0;
	const denials = report.denial_count || 0;
	const lowKarma = isLowKarmaReport(report);

	// Compacta a propósito: el título ya dice la categoría (con su emoji), así
	// que el chip sobraba, y la hora y los votos caben en una sola línea. Sin
	// botones: tocar el marcador abre la ficha completa.
	return `
    <div class="popup-card" role="dialog" aria-label="Detalle del reporte">
      <h4 class="popup-card__title" style="--badge-color:${color}">
        ${escapeHtml(report.title || catLabel)}
      </h4>
      <p class="popup-card__meta">
        <span>${time}</span>
        <span class="popup-card__sep" aria-hidden="true">·</span>
        <span class="confirmation-count">✓ ${confirmations}</span>
        <span class="denial-count">✗ ${denials}</span>
        ${lowKarma ? '<span class="popup-card__low-karma" title="Baja confianza">⚠️</span>' : ""}
      </p>
    </div>
  `;
}


/**
 * Create and bind a popup to a marker.
 * @param {L.Marker} marker
 * @param {object} report
 * @returns {L.Popup}
 */
export function bindPopup(marker, report) {
	const content = buildPopupContent(report);

	const popup = L.popup({
		className: "popup-card-wrapper",
		maxWidth: 280,
		closeButton: true,
		offset: [0, -18],
		// autoPan DESACTIVADO a propósito: Leaflet desplazaba el mapa por su
		// cuenta para "encajar" la tarjeta (con un relleno de 50 px que en un
		// mapa de 130 px no cabe), y justo después fitPopupToMap la movía otra
		// vez. Se veían los dos saltos seguidos y el resultado quedaba mal.
		autoPan: false,
	});

	marker.bindPopup(popup.setContent(content));

	// En un mapa bajo la tarjeta se cortaba por arriba: se ajusta al hueco
	// disponible (ver popupFit.js).
	marker.on("popupopen", (e) => {
		// OJO: con markercluster el marcador puede no estar añadido al mapa
		// directamente y `marker._map` viene undefined; el popup abierto sí
		// conoce su mapa. Usarlo evita que el ajuste se salte en silencio.
		const map = e.popup?._map || marker._map || e.target?._map;
		fitPopupToMap(map, e.popup, marker);
	});

	return popup;
}

/**
 * Escape HTML entities to prevent XSS.
 */
function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

export { relativeTime };
